import React, { useMemo } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import {
  GfFigmaTemplateData,
  GfFlyerItemComponentPosition,
} from "@swagger/typescript-fetch-goflyer";
import {
  CustomAutocompleteMultiSelect,
  AutocompleteOption,
} from "../../components/CustomAutocomplete";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import {
  StyledDialog,
  ModalTitleBar,
  TitleWrap,
  SummaryCard,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  ActionsBar,
  PrimaryButton,
  SecondaryButton,
  tokens,
} from "./styled/EditTemplateModal-styled";
import { useBatchEditModal } from "./hooks/useBatchEditModal";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";

export interface BatchEditModalProps {
  open: boolean;
  onClose: () => void;
  items: GfFlyerItemComponentPosition[];
  onUpdated?: () => void;
}

export const BatchEditModal: React.FC<BatchEditModalProps> = ({
  open,
  onClose,
  items,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const {
    options,
    selectedTemplate,
    handleTemplateSelect,
    loading,
    saving,
    error,
    validationError,
    handleSearch,
    handleSave,
    isValid,
  } = useBatchEditModal({ open, items, onClose, onUpdated });

  const summaryInfo = useMemo(() => {
    if (!items || items.length === 0) {
      return {
        count: 0,
        storeName: "-",
        storeCount: 0,
        categories: [],
        currentTemplates: [],
      };
    }

    const storeMap = new Map<string, string>();

    items.forEach((item) => {
      if (item.store?.id) {
        const storeName = `${item.store.merchant?.name || ""} - ${
          item.store.signature || ""
        }`.trim();
        storeMap.set(item.store.id, storeName);
      } else if (
        item.figmaTemplateData?.stores &&
        item.figmaTemplateData.stores.length > 0
      ) {
        item.figmaTemplateData.stores.forEach((store) => {
          if (store.id) {
            const storeName = `${store.merchant?.name || ""} - ${
              store.signature || ""
            }`.trim();
            storeMap.set(store.id, storeName);
          }
        });
      }
    });

    const storeCount = storeMap.size;
    const storeName = storeCount === 1 ? Array.from(storeMap.values())[0] : "";

    const categories = Array.from(
      new Set(
        items.map((item) => {
          if (item.frontPage) return "Front-Page";
          return item.flyerItemCategory?.name || "-";
        })
      )
    );

    const currentTemplates = Array.from(
      new Set(
        items
          .map((item) => item.figmaTemplateData?.name)
          .filter((name): name is string => !!name)
      )
    );

    return {
      count: items.length,
      storeName,
      storeCount,
      categories,
      currentTemplates,
    };
  }, [items]);

  const disableSave = saving || !selectedTemplate || !isValid;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="batch-edit-title"
      aria-describedby="batch-edit-description"
    >
      <DialogTitle id="batch-edit-title" component={ModalTitleBar as any}>
        <TitleWrap>
          <Typography
            variant="h6"
            component="h2"
            data-testid="batch-edit-modal-title"
          >
            {t(messages.Batch_Edit_Templates())}
          </Typography>
          <Typography
            id="batch-edit-description"
            variant="body2"
            color="text.secondary"
          >
            {t(messages.Update_Template_For_Multiple_Component_Positions())}
          </Typography>
        </TitleWrap>
      </DialogTitle>

      <DialogContent dividers aria-busy={saving}>
        <Stack spacing={2} sx={{ overflow: "hidden" }}>
          {validationError && (
            <Alert
              severity="error"
              role="alert"
              data-testid="batch-edit-validation-error"
            >
              {validationError}
            </Alert>
          )}

          <SummaryCard role="group" aria-label="Batch selection summary">
            <Stack spacing={1.25}>
              <SummaryRow>
                <SummaryLabel>{t(messages.Items_Selected())}</SummaryLabel>
                <Divider flexItem orientation="vertical" />
                <SummaryValue data-testid="batch-edit-count">
                  {summaryInfo.count}
                </SummaryValue>
              </SummaryRow>

              <SummaryRow>
                <SummaryLabel>{t(messages.Store())}</SummaryLabel>
                <Divider flexItem orientation="vertical" />
                <SummaryValue data-testid="batch-edit-store">
                  {summaryInfo.storeCount === 0
                    ? "-"
                    : summaryInfo.storeCount === 1
                    ? summaryInfo.storeName
                    : `${summaryInfo.storeCount} different stores selected`}
                </SummaryValue>
              </SummaryRow>

              <SummaryRow>
                <SummaryLabel>{t(messages.Categories())}</SummaryLabel>
                <Divider flexItem orientation="vertical" />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {summaryInfo.categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={category}
                      size="small"
                      variant="outlined"
                      data-testid={`batch-edit-category-${index}`}
                    />
                  ))}
                </Box>
              </SummaryRow>

              <SummaryRow>
                <SummaryLabel>{t(messages.Current_Templates())}</SummaryLabel>
                <Divider flexItem orientation="vertical" />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {summaryInfo.currentTemplates.length > 0 ? (
                    summaryInfo.currentTemplates.map((template, index) => (
                      <Chip
                        key={index}
                        label={template}
                        size="small"
                        variant="outlined"
                        data-testid={`batch-edit-current-template-${index}`}
                        sx={{ color: tokens.brand }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </Box>
              </SummaryRow>
            </Stack>
          </SummaryCard>

          {!loading && error && (
            <Typography
              color="error"
              role="alert"
              data-testid="batch-edit-error-message"
            >
              {error}
            </Typography>
          )}

          {isValid && (
            <Box sx={{ minHeight: "100px" }}>
              <CustomAutocompleteMultiSelect<GfFigmaTemplateData>
                title={t(messages.New_Template())}
                optionsList={
                  options as AutocompleteOption<GfFigmaTemplateData>[]
                }
                selectedValues={
                  selectedTemplate
                    ? [
                        {
                          label:
                            selectedTemplate.name ||
                            selectedTemplate.id ||
                            "Unnamed",
                          value: selectedTemplate,
                        },
                      ]
                    : []
                }
                multiSelect={false}
                isLoading={loading}
                handleOnSearch={handleSearch}
                handleOnChange={handleTemplateSelect}
                dataTestId="batch-template-autocomplete"
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <ActionsBar>
          <SecondaryButton
            onClick={onClose}
            variant="outlined"
            color="inherit"
            aria-label="Cancel"
            data-testid="batch-edit-cancel-button"
          >
            {t(messages.Cancel())}
          </SecondaryButton>
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["figmaComponentLayout"]}
          >
            <PrimaryButton
              onClick={handleSave}
              variant="contained"
              disabled={disableSave}
              aria-disabled={disableSave}
              data-testid="batch-edit-save-button"
            >
              {t(messages.Save())}
            </PrimaryButton>
          </PermissionComponentWrapper>
        </ActionsBar>
      </DialogActions>
    </StyledDialog>
  );
};

export default BatchEditModal;
